import { createTheme } from '@mui/material/styles';

// Palette WowChePrezzi: stessi colori del brand, allineati al design system del FE application.
// Primario navy #0b3d87, accento azzurro #0587D1 (come application), superfici slate.
const NAVY = '#0b3d87';
const NAVY_DARK = '#082d63';
const NAVY_SOFT = '#345ea3';
const ACCENT = '#0587d1'; // azzurro di application (focus/accenti)
const ACCENT_SOFT = '#e5f6fd';
const INK = '#1e293b';
const MUTED = '#64748b';
const LINE = '#e2e8f0';
const LINE_HOVER = '#cbd5e1';
const SURFACE = '#f8fafc';
const SURFACE_HOVER = '#f1f5f9';
const PAGE = '#f7f6f6';

const FONT = '"Rubik", sans-serif';

const theme = createTheme({
  palette: {
    primary: { main: NAVY, dark: NAVY_DARK, light: NAVY_SOFT, contrastText: '#ffffff' },
    info: { main: ACCENT, light: ACCENT_SOFT, contrastText: '#ffffff' },
    error: { main: '#e00036' },
    success: { main: '#10b981' },
    warning: { main: '#f59e0b' },
    text: { primary: INK, secondary: MUTED },
    divider: LINE,
    background: { default: PAGE, paper: '#ffffff' },
  },
  typography: {
    fontFamily: FONT,
    button: { textTransform: 'none', fontWeight: 600 },
    h4: { fontWeight: 800, letterSpacing: '-0.02em' },
    h5: { fontWeight: 800, letterSpacing: '-0.01em' },
    h6: { fontWeight: 700 },
    subtitle2: { fontWeight: 600 },
  },
  components: {
    // Pulsanti: niente maiuscolo forzato, forma morbida, hover discreto (no scale aggressivi)
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontWeight: 600,
          borderRadius: 12,
          padding: '8px 20px',
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
        },
        containedPrimary: {
          boxShadow: '0 4px 12px rgba(11, 61, 135, 0.18)',
          '&:hover': {
            backgroundColor: NAVY_DARK,
            boxShadow: '0 6px 16px rgba(11, 61, 135, 0.24)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderColor: LINE,
          '&:hover': { borderColor: LINE_HOVER, backgroundColor: SURFACE },
        },
        sizeLarge: { padding: '11px 26px', fontSize: '0.95rem' },
        sizeSmall: { padding: '5px 14px' },
      },
    },
    // Campi di input: superficie soffusa, angoli morbidi, focus ring brand
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: SURFACE,
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: LINE,
            transition: 'border-color 0.2s ease',
          },
          '&:hover': { backgroundColor: SURFACE_HOVER },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: LINE_HOVER },
          '&.Mui-focused': {
            backgroundColor: '#ffffff',
            boxShadow: '0 0 0 4px rgba(11, 61, 135, 0.08)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: NAVY,
            borderWidth: 2,
          },
        },
        input: { fontFamily: FONT },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontFamily: FONT, color: MUTED, '&.Mui-focused': { color: NAVY } },
      },
    },
    // Tooltip minimali: testo breve, contrasto leggibile, niente decorazioni pesanti
    MuiTooltip: {
      defaultProps: {
        arrow: false,
        enterDelay: 500,
        leaveDelay: 0,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(30, 41, 59, 0.94)',
          color: '#f8fafc',
          fontFamily: FONT,
          fontSize: '0.8125rem',
          fontWeight: 400,
          lineHeight: 1.45,
          borderRadius: 6,
          padding: '5px 10px',
          maxWidth: 240,
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.18)',
        },
        arrow: {
          color: 'rgba(30, 41, 59, 0.94)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontFamily: FONT, fontWeight: 600, borderRadius: 8 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 10, transition: 'background-color 0.2s ease, color 0.2s ease' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 20, boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18)' },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
          marginTop: '6px',
          border: `1px solid ${LINE}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: '0.9rem',
          fontWeight: 500,
          padding: '10px 18px',
          transition: 'all 0.15s ease',
          '&:hover': { backgroundColor: SURFACE_HOVER, color: NAVY },
        },
      },
    },
    MuiSelect: {
      styleOverrides: { select: { fontFamily: FONT } },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
          marginTop: '6px',
        },
      },
    },
  },
});

export default theme;
